import { ImageCaption } from "@components/atoms/ImageCaption"
import { NavArrowButton, NavArrows } from "@components/atoms/NavArrows"
import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { SnapScroller } from "@components/atoms/SnapScroller"
import { Button } from "@components/ui/Button"
import { ResponsiveImageFragment } from "@generated-types"
import { cn, tv } from "@utils/tw"
import { EditableText } from "eddev/blocks"
import { AnimatePresence, motion } from "motion/react"
import { ReactNode, useRef, useState } from "react"

type Props = {
  gridInsert?: boolean
  scrollerClass?: string
  className?: string
  images: ResponsiveImageFragment[]
  enableCaptions?: boolean
}

export function GallerySlider(props: Props) {
  const scroller = useRef<SnapScroller>(null)
  const [canScroll, setCanScroll] = useState({ left: false, right: false })
  const [index, setIndex] = useState(0)
  const styles = createStyles({ gridInset: props.gridInsert })

  return (
    <div className={cn(styles.base(), props.className)}>
      <SnapScroller
        scrollerRef={scroller}
        onCanScrollChange={setCanScroll}
        onSlideChange={setIndex}
        className={cn(styles.scroller(), props.scrollerClass)}
        speed="slow"
      >
        {props.images.map((img, i) => (
          <div key={i} className="h-full flex flex-col" onClick={() => setIndex(i)}>
            <ResponsiveImage {...img} className={styles.image({ active: i === index })} />
            {env.admin && props.enableCaptions && (
              <div className="flex-none">
                <EditableText
                  as={ImageCaption}
                  store={`caption.${img.mediaItemUrl}`}
                  placeholder="Enter an optional caption"
                  defaultValue={img.caption!}
                />
              </div>
            )}
          </div>
        ))}
      </SnapScroller>

      <div className="relative-0 flex items-start justify-between w-grid mx-auto pt-4">
        {/* Caption area */}
        <div className="type-body-s text-fg/50 flex px-0 mx-0 items-start">
          {!env.admin && (
            <>
              <div className="inline-block w-[4em] tabular-nums flex-none">
                {index + 1}/{props.images.length}
              </div>
              {props.enableCaptions && (
                <AnimatePresence mode="popLayout" initial={false}>
                  <motion.div
                    key={props.images[index]!.mediaItemUrl}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-pretty h-[3em]"
                  >
                    <EditableText
                      as="span"
                      store={`caption.${props.images[index]!.mediaItemUrl}`}
                      defaultValue={props.images[index]!.caption!}
                    />
                  </motion.div>
                </AnimatePresence>
              )}
            </>
          )}
        </div>
        {/* Nav buttons */}
        <div className="flex items-center justify-between gap-3">
          <Button disabled={!canScroll.left} onClick={() => scroller.current?.go(-1)} icon="chevronLeft" shape="icon" />
          <Button
            disabled={!canScroll.right}
            onClick={() => scroller.current?.go(1)}
            icon="chevronRight"
            shape="icon"
          />
        </div>
      </div>
    </div>
  )
}

const createStyles = tv({
  base: "relative isolate",
  slots: {
    arrowsLayout: "w-full relative mx-6",
    scroller: "[&>*]:snap-start",
    image: "h-[50vw] md:h-[40vw] lg:h-[40vw] md:max-h-[600px] transition-opacity",
  },
  variants: {
    gridInset: {
      true: {
        base: "",
        scroller: "scroll-p-grid-margin-full",
        arrowsLayout: "w-grid mx-auto",
      },
    },
    active: {
      true: {
        // image: "opacity-100",
      },
      false: {
        // image: "opacity-60",
      },
    },
  },
})
